import { useLazyQuery } from "@apollo/client";
import cn from "classnames";
import { endOfDay, isAfter, isPast, startOfDay } from "date-fns";
import isEqual from "lodash/isEqual";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";

import { GET_ALL_PAGES_USER } from "@api/queriesV2/cms";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { getPageTypes } from "@config/cms";
import { blurDataURL } from "@config/config";
import { useStore } from "@hooks/useZustandProvider";
import Editor from "@ui-kit/cms/Editor/Editor";
import Container from "@ui-kit/Container/Container";
import Countdown from "@ui-kit/Countdown/Countdown";
import Loading from "@ui-kit/Loading/Loading";

import s from "./PromosBlock.module.scss";

const PromoItem = ({ isActive = true, promo }) => {
  const { t } = useTranslation();
  const { title: internalTitle } = promo.data;
  const { title, description } = promo.data.seo;
  const { blockBackgroundImage } = promo.data.pageData.blocks.find(
    (block) => block.name === "hero",
  ).data;
  const { salesPeriodFrom, salesPeriodTo } = promo.data.pageData.blocks.find(
    (block) => block.name === "promo",
  ).data;
  return (
    <Link
      href={`${getPageTypes().promo}/${promo.data.slug}`}
      className={s.promo}
    >
      <Image
        className={s.image}
        fill
        style={{
          objectFit: "cover",
          objectPosition: "center",
        }}
        alt={internalTitle}
        sizes="(max-width: 1024px) 100vw, 1260px"
        blurDataURL={blurDataURL}
        src={blockBackgroundImage || promo.data.seo.thumbnailBig}
      />
      <h3 className={s.title}>{title}</h3>
      <div className={cn(s.subTitle, !isActive && s.passed)}>{description}</div>
      {isActive && (
        <div className={s.countdownBlock}>
          {isAfter(new Date(), startOfDay(new Date(salesPeriodFrom))) ||
          isEqual(new Date(), new Date(salesPeriodFrom)) ? (
            <>
              <h6>{t("campaign ends in")}:</h6>
              <Countdown
                dateTo={endOfDay(new Date(salesPeriodTo))}
                className={s.countdown}
                size={3}
                color="white"
              />
            </>
          ) : (
            <>
              <h5>{t("campaign starts in")}:</h5>
              <Countdown
                dateTo={startOfDay(new Date(salesPeriodFrom))}
                className={s.countdown}
                size={3}
                color="white"
              />
            </>
          )}
        </div>
      )}
    </Link>
  );
};

const PromosBlock = ({ data, updateField, settings }) => {
  const { t } = useTranslation();
  const { cms } = useStore();
  const { lang } = cms.page;
  const { isEditMode } = cms.uiOptions;
  const { locale } = useRouter();
  const { blockType } = settings.common;
  const { showActive, showPassed } = settings.specific;
  const { title, activeTitle, passedTitle } = data;
  const [promos, setPromos] = useState({
    active: [],
    passed: [],
  });

  const [getAllPages, { loading }] = useLazyQuery(GET_ALL_PAGES_USER, {
    context: { clientName: "v2" },
    fetchPolicy: "network-only",
    variables: {
      language: isEditMode ? lang : locale,
      type: getPageTypes().promo,
    },
    onCompleted: (data) => {
      if (data.getAllPagesUser?.length) {
        const active = [];
        const passed = [];
        data.getAllPagesUser
          .filter((p) =>
            p.data.pageData.blocks.find((block) => block.name === "promo"),
          )
          .forEach((promo) => {
            const { salesPeriodFrom, salesPeriodTo } =
              promo.data.pageData.blocks.find(
                (block) => block.name === "promo",
              ).data;
            const isPassed =
              isAfter(new Date(salesPeriodFrom), new Date(salesPeriodTo)) ||
              isPast(endOfDay(new Date(salesPeriodTo)));

            isPassed ? passed.push(promo) : active.push(promo);
          });

        return setPromos({
          active,
          passed,
        });
      }

      setRoutes([]);
    },
  });

  useEffect(() => {
    getAllPages();
  }, []);

  return (
    <Container className={s.container}>
      <Container full={false} className={s.innerContaier}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          {loading ? (
            <Loading />
          ) : promos.active.length || promos.passed.length ? (
            <>
              {showActive?.value && promos.active.length && (
                <div className={s.list}>
                  <h3 className={s.head}>
                    <Editor
                      field={{ activeTitle }}
                      onChange={updateField}
                      onlyText
                    />
                  </h3>
                  {promos.active.map((promo, key) => (
                    <PromoItem promo={promo} key={key} />
                  ))}
                </div>
              )}
              {showPassed?.value && promos.passed.length && (
                <div className={s.list}>
                  <h3 className={s.head}>
                    <Editor
                      field={{ passedTitle }}
                      onChange={updateField}
                      onlyText
                    />
                  </h3>
                  {promos.passed.map((promo, key) => (
                    <PromoItem isActive={false} promo={promo} key={key} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div>No promos ):</div>
          )}
        </section>
      </Container>
    </Container>
  );
};

export default PromosBlock;
